import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { VideoCamera, Calendar, Clock, Stethoscope, GoogleLogo, CreditCard, WhatsappLogo } from '@phosphor-icons/react';
import { openRazorpayCheckout } from '../utils/razorpay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookVideoConsultation() {
  const { doctorId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(15);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  const [showManualUpi, setShowManualUpi] = useState(false);
  const [manualTxnId, setManualTxnId] = useState('');

  useEffect(() => {
    fetchDoctor();
  }, [doctorId]);

  const fetchDoctor = async () => {
    try {
      const { data } = await axios.get(`${API}/doctors`);
      const found = (data.doctors || []).find(d => d.id === doctorId);
      setDoctor(found);
    } catch (e) {
      toast.error('Failed to load doctor');
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const fee = doctor ? (doctor.consultation_fee || 700) * (duration / 15) : 0;

  const handleBook = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }
    if (user.role !== 'patient') {
      toast.error('Only patients can book consultations');
      return;
    }

    const scheduledAt = `${date}T${time}:00`;
    setLoading(true);

    try {
      const { data } = await axios.post(
        `${API}/video-consultations`,
        { doctor_id: doctorId, scheduled_at: scheduledAt, duration_minutes: duration, reason },
        { withCredentials: true }
      );
      setBookingId(data.consultation_id);
      toast.success('Booking created! Proceed to payment.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePayRazorpay = async () => {
    setLoading(true);
    openRazorpayCheckout({
      amount: fee,
      purpose: 'video_consult',
      referenceId: bookingId,
      onSuccess: () => {
        toast.success('Payment successful! Meeting link is ready in your dashboard.');
        navigate(`/video-consult/${bookingId}`);
      },
      onFailure: () => setLoading(false),
    });
  };

  const submitManualUpi = async () => {
    if (!manualTxnId.trim() || manualTxnId.trim().length < 6) {
      toast.error('Please enter a valid UPI transaction/reference ID');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/payments/manual-upi/confirm`,
        {
          reference_id: bookingId,
          purpose: 'video_consult',
          amount: fee,
          transaction_id: manualTxnId.trim(),
        },
        { withCredentials: true }
      );
      toast.success('Payment confirmation submitted. Our team will verify shortly.');
      navigate(`/video-consult/${bookingId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit payment confirmation');
    } finally {
      setLoading(false);
    }
  };

  if (!doctor) {
    return (
      <div className="min-h-screen bg-surface-secondary">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen pt-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="book-video-consult-page">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="card mb-6">
            <div className="flex items-center gap-3 mb-3">
              <VideoCamera size={28} weight="duotone" className="text-accent" />
              <h1 className="font-manrope text-2xl font-bold text-primary">Book Video Consultation</h1>
            </div>
            <div className="border-t border-primary/10 pt-4 mt-3 flex items-center gap-3">
              <Stethoscope size={32} weight="duotone" className="text-primary" />
              <div>
                <p className="font-semibold text-primary">{doctor.name}</p>
                <p className="text-sm text-secondary">{doctor.specialty} · {doctor.credentials}</p>
                <p className="text-xs text-secondary">{doctor.languages || 'English, Hindi'}</p>
              </div>
            </div>
          </div>

          {!bookingId ? (
            <div className="card">
              <form onSubmit={handleBook} className="space-y-4" data-testid="book-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-secondary mb-1">
                      <Calendar size={16} weight="duotone" /> Date
                    </label>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      min={today}
                      required
                      className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      data-testid="booking-date"
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-secondary mb-1">
                      <Clock size={16} weight="duotone" /> Preferred Time (IST)
                    </label>
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      required
                      className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      data-testid="booking-time"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Duration</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[15, 30, 60].map(d => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDuration(d)}
                        className={`py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                          duration === d ? 'border-primary bg-primary text-white' : 'border-primary/20 text-secondary'
                        }`}
                        data-testid={`duration-${d}`}
                      >
                        {d} min · ₹{(doctor.consultation_fee || 700) * (d / 15)}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Reason for Consultation</label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    required
                    placeholder="Briefly describe your concern, current diagnosis, or what you want to discuss"
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid="booking-reason"
                  />
                </div>

                <div className="bg-surface-secondary rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-secondary">Total Fee</p>
                    <p className="text-2xl font-bold text-primary font-manrope">₹{fee}</p>
                  </div>
                  <p className="text-xs text-secondary">{duration} min · {doctor.specialty}</p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-3 disabled:opacity-50"
                  data-testid="book-submit-button"
                >
                  {loading ? 'Booking...' : 'Confirm Booking & Pay'}
                </button>
              </form>
            </div>
          ) : (
            <div className="card text-center">
              <h3 className="font-manrope text-xl font-bold text-primary mb-3">Booking Confirmed!</h3>
              <p className="text-secondary mb-6">Complete payment to activate your video consultation slot.</p>

              <button
                onClick={handlePayRazorpay}
                disabled={loading}
                className="w-full flex items-center justify-between gap-3 p-4 mb-3 rounded-lg border-2 border-primary bg-primary/5 hover:bg-primary/10"
                data-testid="pay-razorpay"
              >
                <div className="flex items-center gap-3">
                  <CreditCard size={28} weight="duotone" className="text-primary" />
                  <div className="text-left">
                    <p className="font-semibold text-primary">Pay with Razorpay (Recommended)</p>
                    <p className="text-xs text-secondary">Cards, UPI, Netbanking · Auto-confirmation</p>
                  </div>
                </div>
                <span className="font-bold text-primary">₹{fee}</span>
              </button>

              <a
                href={`upi://pay?pa=shivaam.kesarwaani-1@oksbi&pn=SecondDoctorOpinion&am=${fee}&cu=INR&tn=${encodeURIComponent('VideoConsult-' + bookingId)}`}
                className="w-full flex items-center justify-between gap-3 p-4 mb-3 rounded-lg border-2 border-primary/20 hover:border-primary hover:bg-primary/5"
                data-testid="pay-gpay"
              >
                <div className="flex items-center gap-3">
                  <GoogleLogo size={28} weight="duotone" className="text-primary" />
                  <div className="text-left">
                    <p className="font-semibold text-primary">Pay with Google Pay / UPI</p>
                    <p className="text-xs text-secondary">shivaam.kesarwaani-1@oksbi</p>
                  </div>
                </div>
                <span className="font-bold text-primary">₹{fee}</span>
              </a>

              <a
                href={`https://wa.me/918840804798?text=${encodeURIComponent('Hi, I have booked a video consultation. Booking ID: ' + bookingId)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center py-3 rounded-lg text-sm font-medium text-white"
                style={{ backgroundColor: '#25D366' }}
              >
                <WhatsappLogo size={18} weight="fill" className="inline mr-1" /> Confirm Payment on WhatsApp
              </a>

              <button
                type="button"
                onClick={() => setShowManualUpi(!showManualUpi)}
                className="w-full mt-4 text-sm text-primary underline hover:no-underline"
                data-testid="toggle-manual-upi"
              >
                {showManualUpi ? 'Hide UPI Transaction ID form' : 'Already paid via UPI? Submit Transaction ID'}
              </button>

              {showManualUpi && (
                <div className="mt-4 p-4 bg-surface-secondary rounded-lg border border-primary/20 space-y-3 text-left" data-testid="manual-upi-form">
                  <label className="block text-sm font-medium text-primary">UPI Transaction / Reference ID</label>
                  <input
                    type="text"
                    value={manualTxnId}
                    onChange={(e) => setManualTxnId(e.target.value)}
                    placeholder="e.g., 401123456789"
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid="manual-txn-id-input"
                  />
                  <button
                    type="button"
                    onClick={submitManualUpi}
                    disabled={loading}
                    className="w-full btn-primary py-2 disabled:opacity-50"
                    data-testid="submit-manual-upi-button"
                  >
                    {loading ? 'Submitting...' : 'Submit for Verification'}
                  </button>
                </div>
              )}

              <p className="text-xs text-secondary mt-4">
                After payment, your meeting link will appear in your dashboard. Both you and the doctor will join from there.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
